/**
 * PriorityFlag — the triage flag control shared by the Product Dev list rows
 * and the detail-page header.
 *
 * Behaviour (DESIGN.md inventory-app language):
 *  - `urgent` → filled rose flag; `high` → filled amber flag.
 *  - `null` → NOTHING at rest (no grey noise in the column). When editable it
 *    reveals a faint outline flag so the operator has a hit target. With
 *    `revealOnHover` that affordance stays hidden until the row is hovered /
 *    focused (list); the detail header shows it always.
 *  - Editable: a single click opens a DropdownMenu with Urgent / High / No
 *    priority — one click sets the level (no more click-to-cycle-and-wait).
 *  - Read-only: renders a static glyph.
 *
 * Optimistic feel: the trigger reflects the chosen level immediately (internal
 * `optimistic` state) and reconciles when the parent's `priority` prop settles.
 * The parent still owns the mutation via `onCycle(next)`.
 *
 * Public props (unchanged so the list usage keeps working):
 *  - `priority`      current level (or null)
 *  - `canEdit`       gate — false renders a static glyph
 *  - `onCycle`       called with the newly chosen level (kept this name for
 *                    back-compat; it now receives the picked value, not a cycle)
 *  - `revealOnHover` hide the empty-state affordance until group-hover / focus
 *  - `className`     passthrough
 */
import { useEffect, useState } from 'react';
import { Flag, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  PRIORITY_ICON_CLASS,
  type ProductDevPriority,
} from '@/features/product-dev/utils/status-style';

// Menu order: loudest first, clear last. `null` = "No priority".
const PRIORITY_OPTIONS: Array<ProductDevPriority | null> = ['urgent', 'high', null];

export function PriorityFlag({
  priority,
  canEdit,
  onCycle,
  revealOnHover = false,
  className,
}: {
  priority: ProductDevPriority | null;
  canEdit: boolean;
  /** Called with the picked level. May return a promise — a rejection reverts
   *  the optimistic paint so a failed save can't leave the flag lying. */
  onCycle: (next: ProductDevPriority | null) => void | Promise<unknown>;
  /** Hide the empty-state affordance until the containing `group` is hovered
   *  or the button is focused. List rows pass this; the detail header does not. */
  revealOnHover?: boolean;
  className?: string;
}) {
  const { t } = useTranslation('productDev');

  // Optimistic reflection of the pending choice — reconciles when the prop
  // settles (server round-trip / cache invalidation).
  const [optimistic, setOptimistic] = useState(priority);
  useEffect(() => setOptimistic(priority), [priority]);

  // Nothing to show and nothing to set — render no node at all.
  if (!optimistic && !canEdit) return null;

  const levelLabel = optimistic ? t(`priority.${optimistic}`) : t('priority.none');
  const iconClass = optimistic
    ? PRIORITY_ICON_CLASS[optimistic]
    : 'text-muted-foreground/50';

  if (!canEdit) {
    return (
      <span
        className={cn('inline-flex shrink-0', className)}
        title={levelLabel}
        aria-label={t('priority.ariaLabel', { level: levelLabel })}
      >
        <Flag className={cn('h-3.5 w-3.5', iconClass)} aria-hidden="true" />
      </span>
    );
  }

  const choose = (next: ProductDevPriority | null) => {
    if (next === optimistic) return;
    const previous = optimistic;
    setOptimistic(next); // paint immediately
    // Parent owns the mutation. On rejection, revert the paint — otherwise a
    // failed save leaves the flag lying at the attempted value indefinitely
    // (the prop never changes, so the reconcile effect never refires).
    void Promise.resolve(onCycle(next)).catch(() => setOptimistic(previous));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          // Keep the flag out of the row-navigation click it sits inside.
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'inline-flex shrink-0 items-center rounded-sm p-0.5 transition-opacity duration-200 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:opacity-100',
            !optimistic &&
              revealOnHover &&
              'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
            className,
          )}
          aria-label={t('priority.setPriorityAria', { level: levelLabel })}
          title={optimistic ? t('priority.tooltip', { level: levelLabel }) : t('priority.setTooltip')}
        >
          <Flag className={cn('h-3.5 w-3.5', iconClass)} aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-44">
        {PRIORITY_OPTIONS.map((opt) => {
          const optLabel = opt ? t(`priority.${opt}`) : t('priority.none');
          const optIcon = opt ? PRIORITY_ICON_CLASS[opt] : 'text-muted-foreground/50';
          const active = opt === optimistic;
          return (
            <DropdownMenuItem
              key={opt ?? '__none__'}
              onClick={(e) => {
                e.stopPropagation();
                choose(opt);
              }}
              className="flex items-center gap-2 cursor-pointer"
            >
              <Flag className={cn('h-3.5 w-3.5 shrink-0', optIcon)} aria-hidden="true" />
              <span className="flex-1">{optLabel}</span>
              {active && <Check className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
